import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
// Invented purchase records (NOT derived from any user input).
// Each record: first name + last initial + a Mexican city.
const purchases = [{
  name: 'María L.',
  city: 'Monterrey'
}, {
  name: 'Andrea G.',
  city: 'CDMX'
}, {
  name: 'Lupita R.',
  city: 'Guadalajara'
}, {
  name: 'Carmen H.',
  city: 'Querétaro'
}, {
  name: 'Sofía M.',
  city: 'Puebla'
}, {
  name: 'Gabriela P.',
  city: 'León'
}, {
  name: 'Beatriz N.',
  city: 'Mérida'
}, {
  name: 'Patricia V.',
  city: 'Toluca'
}, {
  name: 'Daniela S.',
  city: 'Tijuana'
}, {
  name: 'Elena T.',
  city: 'Cancún'
}, {
  name: 'Verónica O.',
  city: 'Aguascalientes'
}, {
  name: 'Isabel R.',
  city: 'Hermosillo'
}, {
  name: 'Adriana C.',
  city: 'Saltillo'
}, {
  name: 'Mónica B.',
  city: 'Morelia'
}, {
  name: 'Claudia F.',
  city: 'San Luis Potosí'
}, {
  name: 'Norma J.',
  city: 'Chihuahua'
}, {
  name: 'Rosa E.',
  city: 'Cuernavaca'
}, {
  name: 'Lucía A.',
  city: 'Veracruz'
}, {
  name: 'Teresa V.',
  city: 'Oaxaca'
}, {
  name: 'Esperanza D.',
  city: 'Pachuca'
}];
const INITIAL_DELAY_MS = 3000; // delay before the first toast appears
const VISIBLE_MS = 5000; // how long each toast stays on screen
const HIDDEN_MS = 5000; // gap between toasts
type Toast = {
  id: number;
  name: string;
  city: string;
  minutesAgo: number;
};
export const SocialProofToast: React.FC = () => {
  const [toast, setToast] = useState<Toast | null>(null);
  // Refs survive re-renders, so the scheduling loop is never accidentally
  // cancelled by React state cycles or strict-mode double-effects.
  const lastIndexRef = useRef<number>(-1);
  const toastIdRef = useRef<number>(0);
  const timeoutRef = useRef<number | null>(null);
  const mountedRef = useRef<boolean>(false);
  useEffect(() => {
    mountedRef.current = true;
    const pickRandomIndex = () => {
      if (purchases.length <= 1) return 0;
      let next = Math.floor(Math.random() * purchases.length);
      // Avoid showing the same name twice in a row
      while (next === lastIndexRef.current && purchases.length > 1) {
        next = Math.floor(Math.random() * purchases.length);
      }
      return next;
    };
    const showNext = () => {
      if (!mountedRef.current) return;
      const idx = pickRandomIndex();
      lastIndexRef.current = idx;
      const purchase = purchases[idx];
      toastIdRef.current += 1;
      setToast({
        id: toastIdRef.current,
        name: purchase.name,
        city: purchase.city,
        minutesAgo: Math.floor(Math.random() * 9) + 1
      });
      // Hide after VISIBLE_MS, then schedule the next toast after HIDDEN_MS
      timeoutRef.current = window.setTimeout(() => {
        if (!mountedRef.current) return;
        setToast(null);
        timeoutRef.current = window.setTimeout(showNext, HIDDEN_MS);
      }, VISIBLE_MS);
    };
    // Kick off the cycle
    timeoutRef.current = window.setTimeout(showNext, INITIAL_DELAY_MS);
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  return <div className="fixed bottom-4 left-4 z-[60] pointer-events-none max-w-[280px] md:max-w-[320px]" data-id="element-295">
      <AnimatePresence mode="wait" data-id="element-296">
        {toast && <motion.div key={toast.id} initial={{
        opacity: 0,
        x: -24,
        y: 12
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -24,
        transition: {
          duration: 0.3
        }
      }} transition={{
        duration: 0.45,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-[#E8E2D5] p-3 pr-4 flex items-start gap-3" data-id="element-297">
            <div className="shrink-0 w-9 h-9 rounded-full bg-mint-green flex items-center justify-center" data-id="element-298">
              <CheckCircle2 className="w-5 h-5 text-forest-green" data-id="element-299" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-300">
              <p className="text-[13px] text-dark-text leading-tight" data-id="element-301">
                <span className="font-bold" data-id="element-302">{toast.name}</span> de{' '}
                <span className="font-semibold" data-id="element-303">{toast.city}</span>
              </p>
              <p className="text-[12px] text-muted-text leading-tight mt-0.5" data-id="element-304">
                acaba de obtener El Protocolo
              </p>
              <p className="text-[11px] text-forest-green/70 font-medium mt-1" data-id="element-305">
                Hace {toast.minutesAgo} min · Compra verificada
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};